import React from 'react';
import styled from 'styled-components';

import {
  IconAdd,
  IconCancel,
  IconVideoTemplate,
  IconHeightS,
} from '@bynder/icons';
import { findIndex, noop } from 'lodash';
import { CollectionResponse } from '@bynder/collection-picker';
import { Translate, useTranslate } from '@bynder/localization';
import { Badge, Button, Dropdown, Flex, token, Tooltip } from '@bynder/design-system';
import { generateUUID } from '../../helpers/utils';
import { WIDGET_TYPE } from '../../types';
import {
  useAddCollectionWidgetModal,
  useSectionTemplateModal,
} from '../../hooks/useModals';
import { useWidgetMutations } from '../../mutations/widgets/widgets';
import { useCollectionWidgetMutations } from '../../mutations/widgets/collections';
import { useActivePage, useActiveSection } from '../../queries/guides';
import { track } from '../../helpers/analytics';
import { useSectionMutations } from '../../mutations/sections';
import { MAX_SECTIONS_PER_PAGE } from '../../constants/pages';
import { InspectorSetting } from '../../constants/guides';
import { useInspectorStore } from '../../stores/inspectorStore';
import { getWidgetIcon } from './WidgetIcon';

interface VerticalWidgetMenuProps {
  sectionId: string;
  columnIndex: number;
}

export const VerticalWidgetMenu: React.FC<VerticalWidgetMenuProps> = ({
  sectionId,
  columnIndex,
}) => {
  const { activeSection } = useActiveSection();

  return (
    <StyledMenuContainer $isActiveSection={sectionId === activeSection.id}>
      <Dropdown
        minWidth="670px"
        trigger={({ isOpen, ...triggerProps }) => {
          return (
            <Button
              data-testid="widgetsToolbarButton"
              isPressed={isOpen}
              title="Add widget"
              icon={isOpen ? <IconCancel /> : <IconAdd />}
              {...triggerProps}
            />
          );
        }}
        position="bottom-left"
      >
        <AddWidgetOptions sectionId={sectionId} columnIndex={columnIndex} />
      </Dropdown>
    </StyledMenuContainer>
  );
};

const StyledMenuContainer = styled.li<{ $isActiveSection: boolean }>`
  display: ${props => (props.$isActiveSection ? 'auto' : 'none')};
`;

const AddWidgetOptions = ({
  sectionId,
  columnIndex,
}: {
  sectionId: string;
  columnIndex: number;
}) => {
  const { translate } = useTranslate();
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );

  const { addSection } = useSectionMutations();
  const { data: page } = useActivePage();
  const sectionIndex = findIndex(
    page?.sections,
    section => section.id === sectionId,
  );

  const handleAddBlankSection = () => {
    const isNotFirst = sectionIndex !== 0;
    const isNotLast = sectionIndex !== page.sections.length;

    track('Section Added', {
      'Between Sections': isNotFirst && isNotLast,
    });
    const sectionId = generateUUID();
    addSection.mutate({ sectionId, sectionPosition: sectionIndex + 1 });
  };

  const displayCreateGuideModal = useSectionTemplateModal();

  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);

  const { addWidget } = useWidgetMutations(sectionId, columnIndex);
  const { addCollectionWidget } = useCollectionWidgetMutations();

  const onAddCollectionWidget = (collection: CollectionResponse) => {
    const widgetId = generateUUID();
    addCollectionWidget.mutate({
      newWidgetId: widgetId,
      collectionId: collection.databaseId,
      columnIndex,
    });
    toggleWidgetSettings(widgetId);
  };

  const displayAddCollectionModal = useAddCollectionWidgetModal(
    onAddCollectionWidget,
  );

  const toggleWidgetSettings = (widgetId: string) => {
    setActiveInspectorSetting(InspectorSetting.WIDGET_VIEW);
    setWidgetSettings({
      widgetId,
      sectionId,
      columnIndex,
      widgetIndex: 0,
    });
  };

  const onAddWidget = (widgetType: WIDGET_TYPE) => {
    const newWidgetId = generateUUID();

    switch (widgetType) {
      case WIDGET_TYPE.BUTTON: {
        sessionStorage.setItem('newWidgetId', newWidgetId);
        const buttonId = generateUUID();
        addWidget.mutate({
          widgetType,
          newWidgetId,
          performOptimisticUpdate: true,
          widgetProps: { buttonId },
        });
        break;
      }
      case WIDGET_TYPE.CARD: {
        sessionStorage.setItem('newWidgetId', newWidgetId);
        const cardId = generateUUID();
        addWidget.mutate({
          widgetType,
          newWidgetId,
          performOptimisticUpdate: true,
          widgetProps: { cardId },
        });

        break;
      }

      default: {
        sessionStorage.setItem('newWidgetId', newWidgetId);

        addWidget.mutate({
          widgetType,
          newWidgetId,
          performOptimisticUpdate: true,
        });
      }
    }

    toggleWidgetSettings(newWidgetId);
  };

  return (
    <Flex direction="row">
      <DropdownColumn hasBorder>
        <>
          <Dropdown.Item
            isDisabled={page.sections.length >= MAX_SECTIONS_PER_PAGE}
            data-testid="add_section_bottom"
            icon={<IconHeightS />}
            onClick={() => handleAddBlankSection()}
          >
            {translate('WIDGET_MENU.ADD_SECTION_BOTTOM')}
          </Dropdown.Item>
          <Dropdown.Item
            isDisabled={page.sections.length >= MAX_SECTIONS_PER_PAGE}
            data-testid="add_section_bottom"
            icon={<IconVideoTemplate />}
            onClick={displayCreateGuideModal}
          >
            {translate('WIDGET_MENU.ADD_SECTION_TEMPLATE')}
          </Dropdown.Item>
        </>
        <Dropdown.Divider />
        <Dropdown.Item onClick={noop} isDisabled>
          <Translate id="WIDGETS_CONSTANTS.MEDIA_TITLE" />
        </Dropdown.Item>
        <Dropdown.Item
          icon={getWidgetIcon(WIDGET_TYPE.IMAGE)}
          data-testid="addImageButton"
          onClick={() => {
            onAddWidget(WIDGET_TYPE.IMAGE);
          }}
        >
          {translate('WIDGETS_CONSTANTS.MEDIA')}
        </Dropdown.Item>
        <Dropdown.Item
          icon={getWidgetIcon(WIDGET_TYPE.VIDEO)}
          onClick={() => onAddWidget(WIDGET_TYPE.VIDEO)}
        >
          {translate('WIDGETS_CONSTANTS.VIDEO')}
        </Dropdown.Item>
        <Dropdown.Item
          icon={getWidgetIcon(WIDGET_TYPE.AUDIO)}
          onClick={() => onAddWidget(WIDGET_TYPE.AUDIO)}
        >
          {translate('WIDGETS_CONSTANTS.AUDIO')}
        </Dropdown.Item>

        <Dropdown.Item
          icon={getWidgetIcon(WIDGET_TYPE.COLLECTION)}
          data-testid="addCollectionButton"
          onClick={() => {
            displayAddCollectionModal();
          }}
        >
          {translate('WIDGETS_CONSTANTS.COLLECTION')}
        </Dropdown.Item>
      </DropdownColumn>
      <DropdownColumn hasBorder>
        <Dropdown.Item onClick={noop} isDisabled>
          <Translate id="WIDGETS_CONSTANTS.FORMATTING_TITLE" />
        </Dropdown.Item>

        <Dropdown.Item
          data-testid="teaWidgetButton"
          icon={getWidgetIcon(WIDGET_TYPE.TEXT)}
          onClick={() => {
            onAddWidget(WIDGET_TYPE.TEA);
          }}
          badge={
            <Badge color="blue" isPillShaped isSmall>
              {translate('WIDGETS_CONSTANTS.NEW')}
            </Badge>
          }
        >
          {translate('WIDGETS_CONSTANTS.TEA')}
        </Dropdown.Item>

        <Tooltip content={translate('WIDGET_MENU.TABLE_DEPRECATION')}>
          <Dropdown.Item
            icon={getWidgetIcon(WIDGET_TYPE.TABLE)}
            data-testid="tableWidgetButton"
            onClick={() => null}
            isDisabled={true}
          >
            {translate('WIDGETS_CONSTANTS.TABLE')}
          </Dropdown.Item>
        </Tooltip>

        <Dropdown.Item
          data-testid="cardWidgetButton"
          icon={getWidgetIcon(WIDGET_TYPE.CARD)}
          onClick={() => {
            onAddWidget(WIDGET_TYPE.CARD);
          }}
        >
          {translate('WIDGETS_CONSTANTS.CARD')}
        </Dropdown.Item>

        <Dropdown.Item
          data-testid="buttonWidgetButton"
          icon={getWidgetIcon(WIDGET_TYPE.BUTTON)}
          onClick={() => {
            onAddWidget(WIDGET_TYPE.BUTTON);
          }}
        >
          {translate('WIDGETS_CONSTANTS.BUTTON')}
        </Dropdown.Item>

        <Dropdown.Item
          icon={getWidgetIcon(WIDGET_TYPE.SPACER)}
          data-testid="spacerWidgetButton"
          onClick={() => onAddWidget(WIDGET_TYPE.SPACER)}
        >
          {translate('WIDGETS_CONSTANTS.SPACING_DIVIDER')}
        </Dropdown.Item>
      </DropdownColumn>
      <DropdownColumn>
        <>
          <Dropdown.Item onClick={noop} isDisabled>
            <Translate id="WIDGETS_CONSTANTS.EMBED_TITLE" />
          </Dropdown.Item>
          <>
            <Dropdown.Item
              data-testid="youtubeWigetButton"
              icon={getWidgetIcon(WIDGET_TYPE.YOUTUBE)}
              onClick={() => {
                onAddWidget(WIDGET_TYPE.YOUTUBE);
              }}
            >
              {translate('WIDGETS_CONSTANTS.YOUTUBE')}
            </Dropdown.Item>

            <Dropdown.Item
              icon={getWidgetIcon(WIDGET_TYPE.VIMEO)}
              data-testid="vimeoWidgetButton"
              onClick={() => onAddWidget(WIDGET_TYPE.VIMEO)}
            >
              {translate('WIDGETS_CONSTANTS.VIMEO')}
            </Dropdown.Item>
            <Dropdown.Item
              icon={getWidgetIcon(WIDGET_TYPE.FIGMA)}
              data-testid="figmaWidgetButton"
              onClick={() => onAddWidget(WIDGET_TYPE.FIGMA)}
            >
              {translate('WIDGETS_CONSTANTS.FIGMA')}
            </Dropdown.Item>
          </>
          <Dropdown.Divider />
        </>

        <Dropdown.Item onClick={noop} isDisabled>
          <Translate id="WIDGETS_CONSTANTS.COLORS_TITLE" />
        </Dropdown.Item>
        <Dropdown.Item
          icon={getWidgetIcon(WIDGET_TYPE.COLOR_PALETTE)}
          data-testid="colorPaletteWidgetButton"
          onClick={() => onAddWidget(WIDGET_TYPE.COLOR_PALETTE)}
        >
          {translate('WIDGETS_CONSTANTS.COLOR_PALETTE')}
        </Dropdown.Item>
      </DropdownColumn>
    </Flex>
  );
};

const DropdownColumn = styled.div<{ hasBorder?: boolean }>`
  width: 224px;
  border-right: ${props =>
    props.hasBorder ? `1px solid ${token.gray100a};` : 'none'};
`;
